import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ─────────────────────────────────────────────────────────────
   DESIGN TOKENS  (unchanged)
───────────────────────────────────────────────────────────── */
const T = {
  navy: "#0D1F3C",
  blue: "#1A4F8A",
  sky: "#3B82F6",
  green: "#16A34A",
  red: "#DC2626",
  purple: "#7C3AED",
  amber: "#D97706",
  slate: "#475569",
  steel: "#64748B",
  mist: "#F1F5F9",
  border: "#E2E8F0",
  white: "#FFFFFF",
  shadow: "0 4px 24px rgba(13,31,60,0.10)",
  radius: "12px",
};

/* ═══════════════════════════════════════════════════════════
   API SERVICE
   All backend calls in one place.
═══════════════════════════════════════════════════════════ */

const API = "https://cbackend.xraidigital.com/api";

/* NOTE: exact endpoint for "Export Patient Data" (Excel) wasn't
   given in the spec, only "use the existing backend API".
   Adjust this constant if your real endpoint differs. */
const buildExportPatientDataUrl = (campId) =>
  `${API}/technician/export-patient-data/${campId}/`;

const buildExportWhatsappLogsUrl = (campId) =>
  `${API}/technician/export-whatsapp-logs/${campId}/`;

const buildSmartReportUrl = (patientIdsCsv) =>
  `${API}/technician/smart-report/download/?patient_ids=${patientIdsCsv}`;

const buildGeoTagPhotosUrl = (campId) =>
  `${API}/camp-geotag-photos/${campId}/`;

const getAuthHeader = (token) => {
  if (!token) return {};
  if (token.split(".").length === 3) {
    return { Authorization: `Bearer ${token}` };
  }
  return { Authorization: `Token ${token}` };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    ...getAuthHeader(token),
    ...(options.body && !(options.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) throw new Error("Request failed");
  return res.json();
};

/* ── DOWNLOAD HELPERS (generic, reusable) ── */

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadFile = async (url, filename) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, { headers: { ...getAuthHeader(token) } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  downloadBlob(blob, filename);
};

const exportExcel = (url, filename) => downloadFile(url, filename);

/* ── API SERVICE FUNCTIONS ── */

const api = {
  getCamps: () => apiRequest(`${API}/billing-camps/`),

  getSummary: (campId) => apiRequest(`${API}/preview-bill/${campId}/`),

  getPatients: (campId) =>
    apiRequest(`${API}/billing-preview-patients/${campId}/`),

  getClientMedibuddyBill: (clientId, startDate, endDate) =>
    apiRequest(
      `${API}/client-medibuddy-bill/${clientId}/?start_date=${startDate}&end_date=${endDate}`
    ),

  getGeoTagPhotos: (campId) => apiRequest(buildGeoTagPhotosUrl(campId)),

  exportPatientData: (campId) =>
    exportExcel(buildExportPatientDataUrl(campId), `patient-data-${campId}.xlsx`),

  exportWhatsappLogs: (campId) =>
    exportExcel(buildExportWhatsappLogsUrl(campId), `whatsapp-logs-${campId}.xlsx`),

  downloadSmartReport: (patientIdsCsv, filename = "smart-report.pdf") =>
    downloadFile(buildSmartReportUrl(patientIdsCsv), filename),

  downloadGeoTagPhoto: (photo) => {
    const url = photo.image || photo.photo_url || photo.photo || photo.url;
    const filename = photo.file_name || `geotag-${photo.id || Date.now()}.jpg`;
    return downloadFile(url, filename);
  },
};

/* ═══════════════════════════════════════════════════════════
   TOAST SYSTEM
═══════════════════════════════════════════════════════════ */

const ToastContext = createContext(null);
let toastIdCounter = 0;

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const showToast = useCallback(
    (message, type = "success", duration = 3500) => {
      const id = ++toastIdCounter;
      setToasts((prev) => [...prev, { id, message, type }]);
      timers.current[id] = setTimeout(() => removeToast(id), duration);
      return id;
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span>{t.message}</span>
            <button className="toast-close" onClick={() => removeToast(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL STYLE
   Everything from the original file is preserved as-is.
   Only NEW rules are appended at the bottom for the toolbar,
   toasts, geotag modal, and the actions column.
═══════════════════════════════════════════════════════════ */

const GlobalStyle = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

      * { box-sizing: border-box; margin: 0; padding: 0; }

      body {
        background: linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 60%, #F0FDF4 100%);
        font-family: 'Inter', sans-serif;
        min-height: 100vh;
      }

      /* ── TOPBAR ── */
      .topbar {
        background: ${T.navy};
        padding: 0 32px;
        height: 64px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0 2px 16px rgba(13,31,60,0.18);
        position: sticky;
        top: 0;
        z-index: 100;
      }

      .topbar-brand {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .topbar-logo {
        width: 36px;
        height: 36px;
        background: linear-gradient(135deg, ${T.sky}, ${T.blue});
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: 800;
        color: white;
      }

      .topbar-title {
        font-size: 18px;
        font-weight: 700;
        color: white;
        letter-spacing: -0.3px;
      }

      .topbar-right {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .topbar-user {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255,255,255,0.75);
        font-size: 14px;
        font-weight: 500;
      }

      .user-avatar {
        width: 34px;
        height: 34px;
        background: linear-gradient(135deg, ${T.sky}, ${T.purple});
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 700;
        color: white;
      }

      .logout-btn {
        height: 36px;
        border: 1.5px solid rgba(255,255,255,0.25);
        background: rgba(255,255,255,0.08);
        color: white;
        padding: 0 16px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .logout-btn:hover {
        background: rgba(220,38,38,0.85);
        border-color: transparent;
      }

      /* ── ROOT ── */
      .root {
        padding: 28px 32px;
        max-width: 1400px;
        margin: 0 auto;
      }

      /* ── PAGE CARD ── */
      .page-card {
        background: white;
        border-radius: 20px;
        border: 1px solid ${T.border};
        box-shadow: 0 2px 24px rgba(13,31,60,0.07);
        overflow: hidden;
      }

      .page-header {
        padding: 22px 28px;
        border-bottom: 1px solid ${T.border};
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(to right, #FAFBFF, white);
      }

      .page-title {
        font-size: 22px;
        font-weight: 800;
        color: ${T.navy};
        letter-spacing: -0.4px;
      }

      .page-subtitle {
        margin-top: 4px;
        color: ${T.steel};
        font-size: 13px;
        font-weight: 500;
      }

      .page-body { padding: 28px; }

      /* ── SEARCH ── */
      .search-wrap {
        position: relative;
        display: inline-block;
      }

      .search-icon {
        position: absolute;
        left: 13px;
        top: 50%;
        transform: translateY(-50%);
        color: ${T.steel};
        font-size: 15px;
        pointer-events: none;
      }

      .search-input {
        height: 42px;
        border: 1.5px solid ${T.border};
        border-radius: 10px;
        padding: 0 14px 0 38px;
        min-width: 280px;
        outline: none;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: ${T.navy};
        transition: border-color 0.2s;
        background: ${T.mist};
      }

      .search-input:focus {
        border-color: ${T.sky};
        background: white;
      }

      /* ── CAMP GRID ── */
      .camp-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 18px;
      }

      .camp-card {
        border: 1.5px solid ${T.border};
        border-radius: 16px;
        padding: 22px;
        background: white;
        cursor: pointer;
        transition: all 0.22s;
        position: relative;
        overflow: hidden;
      }

      .camp-card::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 4px;
        background: linear-gradient(90deg, ${T.sky}, ${T.purple});
        opacity: 0;
        transition: opacity 0.2s;
      }

      .camp-card:hover {
        transform: translateY(-4px);
        border-color: ${T.sky};
        box-shadow: 0 8px 28px rgba(59,130,246,0.13);
      }

      .camp-card:hover::before { opacity: 1; }

      .camp-name {
        font-size: 17px;
        font-weight: 700;
        color: ${T.navy};
        margin-bottom: 12px;
        line-height: 1.3;
      }

      .camp-sub {
        color: ${T.steel};
        font-size: 13.5px;
        margin-bottom: 7px;
        display: flex;
        align-items: center;
        gap: 7px;
      }

      .camp-footer {
        margin-top: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .badge {
        display: inline-flex;
        padding: 5px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3px;
        text-transform: uppercase;
      }

      .badge-active {
        background: linear-gradient(135deg, #DCFCE7, #BBF7D0);
        color: #166534;
      }

      .camp-arrow {
        color: ${T.sky};
        font-size: 18px;
        font-weight: 700;
      }

      /* ── SUMMARY GRID ── */
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        margin-bottom: 28px;
      }

      .summary-card {
        border-radius: 16px;
        padding: 20px 22px;
        position: relative;
        overflow: hidden;
      }

      .summary-card-green { background: linear-gradient(135deg, #F0FDF4, #DCFCE7); border: 1.5px solid #BBF7D0; }
      .summary-card-blue  { background: linear-gradient(135deg, #EFF6FF, #DBEAFE); border: 1.5px solid #BFDBFE; }
      .summary-card-red   { background: linear-gradient(135deg, #FFF5F5, #FEE2E2); border: 1.5px solid #FECACA; }
      .summary-card-purple{ background: linear-gradient(135deg, #F5F3FF, #EDE9FE); border: 1.5px solid #DDD6FE; }
      .summary-card-amber { background: linear-gradient(135deg, #FFFBEB, #FEF3C7); border: 1.5px solid #FDE68A; }

      .summary-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        margin-bottom: 14px;
      }

      .summary-label {
        font-size: 11px;
        color: ${T.slate};
        margin-bottom: 6px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.5px;
      }

      .summary-value {
        font-size: 32px;
        font-weight: 800;
        letter-spacing: -1px;
        line-height: 1;
      }

      /* ── TABLE ── */
      .table-scroll { overflow-x: auto; border-radius: 12px; border: 1px solid ${T.border}; }

      .table {
        width: 100%;
        border-collapse: collapse;
        min-width: 1100px;
      }

      .table thead tr {
        background: linear-gradient(to right, #F8FAFF, ${T.mist});
      }

      .table th {
        color: ${T.slate};
        padding: 13px 16px;
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.5px;
        border-bottom: 1.5px solid ${T.border};
      }

      .table td {
        padding: 14px 16px;
        border-bottom: 1px solid #F1F5F9;
        font-size: 14px;
        color: ${T.navy};
        vertical-align: middle;
      }

      .table tbody tr:hover { background: #FAFBFF; }
      .table tbody tr:last-child td { border-bottom: none; }

      .patient-name {
        font-weight: 700;
        color: ${T.navy};
      }

      .count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
      }

      .count-green { background: #DCFCE7; color: #166534; }
      .count-red   { background: #FEE2E2; color: #991B1B; }

      /* ── PILLS ── */
      .pill {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11.5px;
        font-weight: 600;
        margin: 2px;
      }

      .pill-green { background: #DCFCE7; color: #166534; }
      .pill-red   { background: #FEE2E2; color: #991B1B; }

      .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3px;
      }

      .status-done    { background: #DCFCE7; color: #166534; }
      .status-pending { background: #FEF3C7; color: #92400E; }

      .medibuddy-btn {
        height: 40px;
        border: none;
        background: linear-gradient(135deg, #16A34A, #15803D);
        color: white;
        padding: 0 18px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13.5px;
        display: flex;
        align-items: center;
        gap: 7px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .medibuddy-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(22,163,74,0.25);
      }

      /* ── BUTTONS ── */
      .back-btn {
        height: 40px;
        border: none;
        background: ${T.navy};
        color: white;
        padding: 0 18px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13.5px;
        display: flex;
        align-items: center;
        gap: 7px;
        transition: background 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .back-btn:hover { background: ${T.blue}; }

      .empty {
        padding: 60px 40px;
        text-align: center;
        color: ${T.steel};
        font-size: 15px;
      }

      .empty-icon { font-size: 40px; margin-bottom: 12px; }

      /* ── RESPONSIVE (original) ── */
      @media (max-width: 900px) {
        .summary-grid { grid-template-columns: 1fr 1fr; }
        .root { padding: 16px; }
      }

      @media (max-width: 600px) {
        .summary-grid { grid-template-columns: 1fr; }
        .topbar { padding: 0 16px; }
        .topbar-title { font-size: 15px; }
        .page-header { flex-direction: column; gap: 14px; align-items: stretch; }
        .search-input { width: 100%; min-width: unset; }
        .search-wrap { width: 100%; }
      }

      /* ─────────────────────────────────────────────────────
         NEW: ACTION TOOLBAR
      ───────────────────────────────────────────────────────── */
      .action-toolbar {
        display: flex;
        flex-wrap: nowrap;
        gap: 10px;
        margin-bottom: 22px;
      }

      .toolbar-btn {
        height: 40px;
        border: 1.5px solid ${T.border};
        background: white;
        color: ${T.navy};
        padding: 0 16px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13.5px;
        display: flex;
        align-items: center;
        gap: 7px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
        white-space: nowrap;
      }

      .toolbar-btn:hover:not(:disabled) {
        border-color: ${T.sky};
        background: #FAFBFF;
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(59,130,246,0.12);
      }

      .toolbar-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      .toolbar-btn-ghost {
        background: ${T.mist};
      }

      /* ─────────────────────────────────────────────────────
         NEW: ACTIONS TABLE COLUMN
      ───────────────────────────────────────────────────────── */
      .row-action-btn {
        height: 32px;
        border: 1.5px solid ${T.border};
        background: white;
        color: ${T.blue};
        padding: 0 12px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
        white-space: nowrap;
      }

      .row-action-btn:hover:not(:disabled) {
        border-color: ${T.sky};
        background: #EFF6FF;
      }

      .row-action-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* ─────────────────────────────────────────────────────
         NEW: TOASTS
      ───────────────────────────────────────────────────────── */
      .toast-stack {
        position: fixed;
        bottom: 24px;
        right: 24px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        z-index: 1000;
      }

      .toast {
        min-width: 260px;
        max-width: 360px;
        background: white;
        border-radius: 12px;
        border: 1.5px solid ${T.border};
        box-shadow: 0 8px 28px rgba(13,31,60,0.16);
        padding: 13px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        font-size: 13.5px;
        font-weight: 600;
        color: ${T.navy};
        animation: toast-in 0.22s ease;
      }

      @keyframes toast-in {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      .toast-success { border-left: 4px solid ${T.green}; }
      .toast-error   { border-left: 4px solid ${T.red}; }

      .toast-close {
        border: none;
        background: transparent;
        color: ${T.steel};
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        padding: 0 2px;
      }

      /* ─────────────────────────────────────────────────────
         NEW: GEOTAG MODAL
      ───────────────────────────────────────────────────────── */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(13,31,60,0.55);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 200;
        padding: 24px;
      }

      .modal-panel {
        background: white;
        border-radius: 20px;
        width: 100%;
        max-width: 1100px;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(13,31,60,0.3);
      }

      .modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid ${T.border};
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(to right, #FAFBFF, white);
      }

      .modal-title {
        font-size: 19px;
        font-weight: 800;
        color: ${T.navy};
      }

      .modal-subtitle {
        margin-top: 3px;
        color: ${T.steel};
        font-size: 12.5px;
        font-weight: 500;
      }

      .modal-close {
        border: none;
        background: ${T.mist};
        color: ${T.navy};
        width: 32px;
        height: 32px;
        border-radius: 8px;
        font-size: 20px;
        cursor: pointer;
        line-height: 1;
      }

      .modal-toolbar {
        padding: 16px 24px;
        display: flex;
        gap: 12px;
        border-bottom: 1px solid ${T.border};
        flex-wrap: wrap;
      }

      .sort-select {
        height: 42px;
        border: 1.5px solid ${T.border};
        border-radius: 10px;
        padding: 0 12px;
        background: ${T.mist};
        color: ${T.navy};
        font-family: 'Inter', sans-serif;
        font-size: 13.5px;
        font-weight: 500;
        outline: none;
      }

      .modal-body {
        padding: 24px;
        overflow-y: auto;
      }

      .geotag-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 16px;
      }

      .geotag-card {
        border: 1.5px solid ${T.border};
        border-radius: 14px;
        overflow: hidden;
        background: white;
        display: flex;
        flex-direction: column;
        transition: box-shadow 0.2s;
      }

      .geotag-card:hover {
        box-shadow: 0 8px 24px rgba(13,31,60,0.1);
      }

      .geotag-image {
        height: 140px;
        background: ${T.mist};
        cursor: pointer;
        overflow: hidden;
      }

      .geotag-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .geotag-image-fallback {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        color: ${T.steel};
      }

      .geotag-info {
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        flex: 1;
      }

      .geotag-row {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-size: 12px;
        color: ${T.navy};
      }

      .geotag-label {
        color: ${T.steel};
        font-weight: 600;
        flex-shrink: 0;
      }

      .geotag-download-btn {
        margin: 0 14px 14px;
        justify-content: center;
      }

      .image-preview-overlay {
        position: fixed;
        inset: 0;
        background: rgba(13,31,60,0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 300;
        padding: 24px;
      }

      .image-preview-full {
        max-width: 90vw;
        max-height: 90vh;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.4);
      }

      /* ── NEW RESPONSIVE: toolbar + geotag grid ── */
      @media (max-width: 900px) {
        .action-toolbar { flex-wrap: wrap; }
        .geotag-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
      }

      @media (max-width: 600px) {
        .action-toolbar {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .toolbar-btn { width: 100%; justify-content: center; }
        .geotag-grid { grid-template-columns: 1fr; }
        .modal-toolbar { flex-direction: column; }
        .toast-stack { left: 16px; right: 16px; bottom: 16px; }
        .toast { min-width: unset; max-width: unset; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
};

/* ─────────────────────────────────────────────────────────────
   TOP BAR  (unchanged)
───────────────────────────────────────────────────────────── */

const TopBar = ({ onLogout }) => {
  return (
    <div className="topbar">
      <div className="topbar-brand">
        <div className="topbar-logo">C</div>
        <span className="topbar-title">CampManager</span>
      </div>

      <div className="topbar-right">
        <button className="logout-btn" onClick={onLogout}>
          ⏻ Logout
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   SUMMARY CARD  (unchanged)
───────────────────────────────────────────────────────────── */

const SummaryCard = ({ label, value, color, bgClass, icon }) => (
  <div className={`summary-card ${bgClass}`}>
    <div className="summary-icon">{icon}</div>
    <div className="summary-label">{label}</div>
    <div className="summary-value" style={{ color }}>{value}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   ACTION TOOLBAR
───────────────────────────────────────────────────────────── */

const ActionToolbar = ({ campId, patients, onOpenGeoTag, onRefresh }) => {
  const { showToast } = useToast();

  const [busy, setBusy] = useState({
    exportPatients: false,
    exportWhatsapp: false,
    smartReports: false,
    refreshing: false,
  });

  const setBusyKey = (key, val) =>
    setBusy((prev) => ({ ...prev, [key]: val }));

  const handleExportPatients = async () => {
    if (busy.exportPatients) return;
    setBusyKey("exportPatients", true);
    try {
      await api.exportPatientData(campId);
      showToast("✅ Patient Data Exported", "success");
    } catch (e) {
      console.error(e);
      showToast("❌ Unable to export", "error");
    } finally {
      setBusyKey("exportPatients", false);
    }
  };

  const handleExportWhatsapp = async () => {
    if (busy.exportWhatsapp) return;
    setBusyKey("exportWhatsapp", true);
    try {
      await api.exportWhatsappLogs(campId);
      showToast("✅ WhatsApp Logs Exported", "success");
    } catch (e) {
      console.error(e);
      showToast("❌ Unable to export", "error");
    } finally {
      setBusyKey("exportWhatsapp", false);
    }
  };

  const handleSmartReports = async () => {
    if (busy.smartReports) return;
    const ids = (patients || [])
        .map((p) => p.unique_patient_id)
        .filter((id) => id);

    if (ids.length === 0) {
      showToast("❌ Unable to export", "error");
      return;
    }

    setBusyKey("smartReports", true);
    try {
      await api.downloadSmartReport(ids.join(","), `smart-reports-${campId}.pdf`);
      showToast("✅ Smart Reports Downloaded", "success");
    } catch (e) {
      console.error(e);
      showToast("❌ Unable to export", "error");
    } finally {
      setBusyKey("smartReports", false);
    }
  };

  const handleRefresh = async () => {
    if (busy.refreshing) return;
    setBusyKey("refreshing", true);
    try {
      await onRefresh();
    } finally {
      setBusyKey("refreshing", false);
    }
  };

  return (
    <div className="action-toolbar">
      {/* <button
        className="toolbar-btn"
        onClick={handleExportPatients}
        disabled={busy.exportPatients}
      >
        📊 {busy.exportPatients ? "Exporting..." : "Export Patient Data"}
      </button> */}

      <button
        className="toolbar-btn"
        onClick={handleExportWhatsapp}
        disabled={busy.exportWhatsapp}
      >
        💬 {busy.exportWhatsapp ? "Exporting..." : "Export WhatsApp Logs"}
      </button>

      {/* <button
        className="toolbar-btn"
        onClick={handleSmartReports}
        disabled={busy.smartReports}
      >
        📄 {busy.smartReports ? "Downloading..." : "Download Smart Reports"}
      </button> */}

      <button className="toolbar-btn" onClick={onOpenGeoTag}>
        📷 GeoTag Photos
      </button>

      <button
        className="toolbar-btn toolbar-btn-ghost"
        onClick={handleRefresh}
        disabled={busy.refreshing}
      >
        🔄 {busy.refreshing ? "Refreshing..." : "Refresh"}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   GEOTAG MODAL
───────────────────────────────────────────────────────────── */

const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest First" },
  { value: "date_asc", label: "Oldest First" },
  { value: "technician", label: "Technician (A–Z)" },
  { value: "type", label: "Photo Type (A–Z)" },
];

const GeoTagModal = ({ campId, onClose }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.getGeoTagPhotos(campId);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.results)
        ? res.results
        : Array.isArray(res?.photos)
        ? res.photos
        : [];
      setPhotos(list);
    } catch (e) {
      console.error(e);
      setPhotos([]);
      showToast("❌ Unable to export", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (photo) => {
    const id = photo.id || photo.file_name;
    setDownloadingId(id);
    try {
      await api.downloadGeoTagPhoto(photo);
      showToast("✅ GeoTag Loaded", "success");
    } catch (e) {
      console.error(e);
      showToast("❌ Unable to export", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = photos.filter((p) => {
      const tech = String(
        p.technician_name ??
        p.technician ??
        ""
        ).toLowerCase();
      const type = (p.photo_type || p.type || "").toLowerCase();
      const address = (p.address || "").toLowerCase();
      return tech.includes(q) || type.includes(q) || address.includes(q);
    });

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.upload_date || 0) - new Date(b.upload_date || 0);
        case "technician":
          return (a.technician || a.technician_name || "").localeCompare(
            b.technician || b.technician_name || ""
          );
        case "type":
          return (a.photo_type || a.type || "").localeCompare(
            b.photo_type || b.type || ""
          );
        case "date_desc":
        default:
          return new Date(b.upload_date || 0) - new Date(a.upload_date || 0);
      }
    });

    return list;
  }, [photos, search, sortBy]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">📷 GeoTag Photos</div>
            <div className="modal-subtitle">
              {photos.length} photo{photos.length !== 1 ? "s" : ""} on file
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-toolbar">
          <div className="search-wrap" style={{ flex: 1 }}>
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              style={{ width: "100%", minWidth: "unset" }}
              placeholder="Search technician, type, or address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading photos...
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📷</div>
              No geotag photos found
            </div>
          ) : (
            <div className="geotag-grid">
              {filtered.map((p, i) => {
                const id = p.id || p.file_name || i;
                const imgUrl = p.image || p.photo_url || p.photo || p.url;
                return (
                  <div className="geotag-card" key={id}>
                    <div
                      className="geotag-image"
                      onClick={() => setPreviewPhoto(p)}
                    >
                      {imgUrl ? (
                        <img src={imgUrl} alt={p.photo_type || "Geotag"} />
                      ) : (
                        <div className="geotag-image-fallback">📷</div>
                      )}
                    </div>
                    <div className="geotag-info">
                      <div className="geotag-row">
                        <span className="geotag-label">Technician</span>
                        <span>{p.technician || p.technician_name || "—"}</span>
                      </div>
                      <div className="geotag-row">
                        <span className="geotag-label">Type</span>
                        <span>{p.photo_type || p.type || "—"}</span>
                      </div>
                      <div className="geotag-row">
                        <span className="geotag-label">Lat / Lng</span>
                        <span>
                          {p.latitude ?? "—"}, {p.longitude ?? "—"}
                        </span>
                      </div>
                      <div className="geotag-row">
                        <span className="geotag-label">Address</span>
                        <span>{p.address || "—"}</span>
                      </div>
                      <div className="geotag-row">
                        <span className="geotag-label">Uploaded</span>
                        <span>
                          {p.upload_date
                            ? new Date(p.upload_date).toLocaleDateString("en-IN")
                            : "—"}
                        </span>
                      </div>
                    </div>
                    <button
                      className="toolbar-btn geotag-download-btn"
                      disabled={downloadingId === id}
                      onClick={() => handleDownload(p)}
                    >
                      {downloadingId === id ? "Downloading..." : "⬇ Download"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {previewPhoto && (
        <div
          className="image-preview-overlay"
          onClick={() => setPreviewPhoto(null)}
        >
          <img
            src={
              previewPhoto.image ||
              previewPhoto.photo_url ||
              previewPhoto.photo ||
              previewPhoto.url
            }
            alt="Preview"
            className="image-preview-full"
          />
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   ACTUAL CASES VIEW  (enhanced)
───────────────────────────────────────────────────────────── */

const ActualCasesView = ({ camp, onBack }) => {
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState({});
  const [geotagCount, setGeotagCount] = useState(0);
  const [showGeoTagModal, setShowGeoTagModal] = useState(false);
  const [rowDownloading, setRowDownloading] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [summaryRes, patientsRes, geotagRes] = await Promise.all([
        api.getSummary(camp.id),
        api.getPatients(camp.id),
        api.getGeoTagPhotos(camp.id).catch(() => []),
      ]);
      setSummary(summaryRes?.summary || {});
      const patientData =
        Array.isArray(patientsRes) ? patientsRes
        : Array.isArray(patientsRes?.patients) ? patientsRes.patients
        : Array.isArray(patientsRes?.results) ? patientsRes.results
        : [];
      setPatients(patientData);

      const geotagList = Array.isArray(geotagRes)
        ? geotagRes
        : Array.isArray(geotagRes?.results) ? geotagRes.results
        : Array.isArray(geotagRes?.photos) ? geotagRes.photos
        : [];
      setGeotagCount(geotagList.length);
    } catch (e) {
      console.error(e);
      setSummary({});
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await load();
    showToast("✅ Dashboard Refreshed", "success");
  };

  const handleRowSmartReport = async (patient) => {
    console.log("Row clicked", patient);

    const uniquePatientId = patient.patient_unique_id;

    if (!uniquePatientId) {
        console.log("Patient ID missing");
        return;
    }

    setRowDownloading(uniquePatientId);

    try {
        await api.downloadSmartReport(
            uniquePatientId,
            `smart-report-${uniquePatientId}.pdf`
        );

        showToast("✅ Smart Report Downloaded", "success");
    } catch (e) {
        console.error(e);
        showToast("❌ Unable to download report", "error");
    } finally {
        setRowDownloading(null);
    }
};

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      (p.patient_name || "").toLowerCase().includes(q) ||
      (p.completed_services || []).join(" ").toLowerCase().includes(q) ||
      (p.pending_services || []).join(" ").toLowerCase().includes(q)
    );
  });

  const completedCases = filtered.filter((p) => p.pending_count === 0).length;
  const pendingCases = filtered.filter((p) => p.pending_count > 0).length;

  const totalCompletedServices = filtered.reduce(
    (sum, p) => sum + (p.completed_count || 0),
    0
  );
  const totalPendingServices = filtered.reduce(
    (sum, p) => sum + (p.pending_count || 0),
    0
  );

  return (
    <div className="root">
      <div className="page-card">
        <div className="page-header">
          <div>
            <div className="page-title">📋 Actual Cases</div>
            <div className="page-subtitle">
              🏥 {camp.branch_name} &nbsp;·&nbsp; 📍 {camp.location}
            </div>
          </div>
          <button className="back-btn" onClick={onBack}>
            ← Back To Camps
          </button>
        </div>

        <div className="page-body">
          {/* SEARCH */}
          <div style={{ marginBottom: 24 }}>
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search patient or service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading patient data...
            </div>
          ) : (
            <>
              {/* SUMMARY */}
              <div className="summary-grid">
                <SummaryCard
                  label="Total Actual Cases"
                  value={summary.actual_cases || patients.length}
                  color={T.green}
                  bgClass="summary-card-green"
                  icon="👥"
                />
                <SummaryCard
                  label="Completed Cases"
                  value={completedCases}
                  color={T.sky}
                  bgClass="summary-card-blue"
                  icon="✅"
                />
                <SummaryCard
                  label="Pending Cases"
                  value={pendingCases}
                  color={T.red}
                  bgClass="summary-card-red"
                  icon="⏰"
                />
                <SummaryCard
                  label="Completion %"
                  value={
                    patients.length
                      ? `${Math.round((completedCases / patients.length) * 100)}%`
                      : "0%"
                  }
                  color={T.purple}
                  bgClass="summary-card-purple"
                  icon="📊"
                />
                <SummaryCard
                  label="Total GeoTag Photos"
                  value={geotagCount}
                  color={T.blue}
                  bgClass="summary-card-blue"
                  icon="📷"
                />
                <SummaryCard
                  label="Completed Services"
                  value={totalCompletedServices}
                  color={T.green}
                  bgClass="summary-card-green"
                  icon="🧪"
                />
                <SummaryCard
                  label="Pending Services"
                  value={totalPendingServices}
                  color={T.amber}
                  bgClass="summary-card-amber"
                  icon="⏳"
                />
              </div>

              {/* ACTION TOOLBAR */}
              <ActionToolbar
                campId={camp.id}
                patients={filtered}
                onOpenGeoTag={() => setShowGeoTagModal(true)}
                onRefresh={handleRefresh}
              />

              {/* TABLE */}
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Patient Name</th>
                      <th>Test Date</th>
                      <th>Completed</th>
                      <th>Pending</th>
                      <th>Completed Services</th>
                      <th>Pending Services</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={9}>
                          <div className="empty">
                            <div className="empty-icon">🔍</div>
                            No patients found
                          </div>
                        </td>
                      </tr>
                    )}
                    {filtered.map((p, index) => {
                      const done = p.pending_count === 0;
                      const rowId = p.patient_unique_id ?? index;
                      return (
                        <tr key={p.id || index}>
                          <td style={{ color: T.steel, fontWeight: 600 }}>
                            {index + 1}
                          </td>
                          <td className="patient-name">
                            {p.patient_name || "—"}
                          </td>
                          <td style={{ color: T.slate }}>
                            {p.test_date
                              ? new Date(p.test_date).toLocaleDateString("en-IN")
                              : "—"}
                          </td>
                          <td>
                            <span className="count-badge count-green">
                              {p.completed_count || 0}
                            </span>
                          </td>
                          <td>
                            <span className="count-badge count-red">
                              {p.pending_count || 0}
                            </span>
                          </td>
                          <td>
                            {(p.completed_services || []).length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {p.completed_services.map((s, i) => (
                                  <span key={i} className="pill pill-green">{s}</span>
                                ))}
                              </div>
                            ) : <span style={{ color: T.steel }}>—</span>}
                          </td>
                          <td>
                            {(p.pending_services || []).length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {p.pending_services.map((s, i) => (
                                  <span key={i} className="pill pill-red">{s}</span>
                                ))}
                              </div>
                            ) : <span style={{ color: T.steel }}>—</span>}
                          </td>
                          <td>
                            <span className={`status-pill ${done ? "status-done" : "status-pending"}`}>
                              {done ? "✅ Done" : "⏳ Pending"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="row-action-btn"
                              disabled={rowDownloading === rowId}
                              onClick={() => handleRowSmartReport(p)}
                            >
                              📄 {rowDownloading === rowId ? "Downloading..." : "Smart Report"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {showGeoTagModal && (
        <GeoTagModal
          campId={camp.id}
          onClose={() => setShowGeoTagModal(false)}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   CAMP LIST PAGE  (unchanged)
───────────────────────────────────────────────────────────── */

const CampsList = ({ onOpenCamp, onOpenMedibuddy }) => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [camps, setCamps] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.getCamps();
      const allCamps = res.results || res;
      const medibuddyCamps = allCamps.filter(
        (camp) => camp.billing_mode === "MEDIBUDDY"
      );
      setCamps(medibuddyCamps);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = camps.filter((c) => {
    const q = search.toLowerCase();
    return (
      (c.branch_name || "").toLowerCase().includes(q) ||
      (c.location || "").toLowerCase().includes(q) ||
      (c.client_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="root">
      <div className="page-card">
        <div className="page-header">
          <div>
            <div className="page-title">🏕️ Camps Actual Cases</div>
            <div className="page-subtitle">
              {camps.length} camp{camps.length !== 1 ? "s" : ""} available
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search camps..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button className="medibuddy-btn" onClick={onOpenMedibuddy}>
              💳 Medibuddy
            </button>
          </div>
        </div>

        <div className="page-body">
          {loading ? (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading camps...
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🔍</div>
              No camps found
            </div>
          ) : (
            <div className="camp-grid">
              {filtered.map((camp) => (
                <div
                  key={camp.id}
                  className="camp-card"
                  onClick={() => onOpenCamp(camp)}
                >
                  <div className="camp-name">{camp.branch_name}</div>
                  <div className="camp-sub">📍 {camp.location}</div>
                  <div className="camp-sub">🏢 {camp.client_name}</div>
                  <div className="camp-sub">
                    📅 {camp.start_date} to {camp.end_date}
                  </div>
                  <div className="camp-footer">
                    <span className="badge badge-active">
                      {camp.billing_mode}
                    </span>
                    <span className="camp-arrow">→</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MEDIBUDDY CLIENT BILLING  (unchanged)
───────────────────────────────────────────────────────────── */

const ClientMedibuddyBillScreen = ({ onBack }) => {
  const CLIENT_ID = 57;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = async () => {
    try {
      if (!startDate || !endDate) {
        alert("Please select date range");
        return;
      }
      if (startDate > endDate) {
        alert("Start date cannot be after end date");
        return;
      }

      setLoading(true);
      const res = await api.getClientMedibuddyBill(CLIENT_ID, startDate, endDate);
      setData(res);
    } catch (e) {
      console.error(e);
      alert("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="root">
      <div className="page-card">
        <div className="page-header">
          <div>
            <div className="page-title">💳 Medibuddy Client Billing</div>
            <div className="page-subtitle">Fetch billing by date range</div>
          </div>
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className="page-body">
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 24,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="date"
              className="search-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              type="date"
              className="search-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <button className="medibuddy-btn" onClick={load} disabled={loading}>
              {loading ? "Loading..." : "🔍 Fetch Bill"}
            </button>
          </div>

          {loading && (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading billing data...
            </div>
          )}

          {!loading && !data && (
            <div className="empty">
              <div className="empty-icon">📅</div>
              Select a date range to fetch billing
            </div>
          )}

          {!loading && data && (
            <div>
              {data.details.map((camp, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: 28,
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    padding: 20,
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: T.navy }}>
                    🏕️ {camp.camp_name}
                  </div>

                  <div className="table-scroll">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {camp.details.map((row, j) => (
                          <tr key={j}>
                            <td>{row.component}</td>
                            <td>{row.units}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginTop: 14, fontSize: 14, color: T.slate }}>
                    Cases: <b>{camp.summary.actual_cases}</b>
                    {" "} | {" "}
                    Days: <b>{camp.summary.camp_days}</b>
                  </div>
                </div>
              ))}

              <div
                style={{
                  background: "linear-gradient(135deg,#EFF6FF,#DBEAFE)",
                  border: "1.5px solid #BFDBFE",
                  borderRadius: 16,
                  padding: 24,
                  marginTop: 20,
                }}
              >
                <div style={{ fontSize: 14, color: T.slate, marginBottom: 8, fontWeight: 700 }}>
                  Client Summary
                </div>
                <div style={{ fontSize: 14, color: T.steel }}>
                  Total Cases: <b>{data.summary.total_cases}</b>
                  {" "} | {" "}
                  Total Camp Days: <b>{data.summary.total_days}</b>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   ROOT
───────────────────────────────────────────────────────────── */

const ActualCasesApp = () => {
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [showMedibuddy, setShowMedibuddy] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  return (
    <>
      <GlobalStyle />
      <TopBar onLogout={handleLogout} />

      {showMedibuddy ? (
        <ClientMedibuddyBillScreen onBack={() => setShowMedibuddy(false)} />
      ) : !selectedCamp ? (
        <CampsList
          onOpenCamp={setSelectedCamp}
          onOpenMedibuddy={() => setShowMedibuddy(true)}
        />
      ) : (
        <ActualCasesView
          camp={selectedCamp}
          onBack={() => setSelectedCamp(null)}
        />
      )}
    </>
  );
};

export default function ActualCasesRoot() {
  return (
    <ToastProvider>
      <ActualCasesApp />
    </ToastProvider>
  );
}